import { CheckIcon, CloseIcon } from "@chakra-ui/icons";
import {
  ButtonGroup,
  Editable,
  EditableInput,
  EditableInputProps,
  EditablePreview,
  EditablePreviewProps,
  EditableProps,
  Flex,
  IconButton,
  useEditableControls,
} from "@chakra-ui/react";
import { forwardRef, useImperativeHandle } from "react";

/** Imperative handle exposed via `ref` on `RanaEditable.EditHandle`. */
export interface RanaEditableHandle {
  /** Programmatically enter edit mode (e.g. from a context-menu "Rename" action). */
  startEditing: () => void;
}

/**
 * A Chakra `Editable` that only activates on double-click.
 *
 * Expose a ref to programmatically trigger edit mode via `RanaEditable.EditHandle`:
 * ```tsx
 * const editRef = useRef<RanaEditableHandle>(null);
 * <RanaEditable defaultValue="My layer" onSubmit={rename}>
 *   <RanaEditable.Preview />
 *   <RanaEditable.Input />
 *   <RanaEditable.EditHandle ref={editRef} />
 * </RanaEditable>
 * // in a menu action:
 * action: () => editRef.current?.startEditing()
 * ```
 *
 * - Double-click to start editing
 * - Enter / click ✓ to confirm
 * - Escape / click ✗ to cancel
 * - Does NOT submit on blur, so accidental clicks outside don't save
 */

export const RanaEditable = (props: EditableProps) => {
  return (
    <Editable
      submitOnBlur={false}
      // Prevent single-click / focus from activating the edit state.
      // Editing is only triggered via the double-click handler below.
      isPreviewFocusable={false}
      selectAllOnFocus={false}
      display="flex"
      {...props}
    />
  );
};

const RanaEditablePreview = (props: EditablePreviewProps) => {
  const { getEditButtonProps } = useEditableControls();
  return (
    <EditablePreview
      onDoubleClick={(e) => {
        e.stopPropagation();
        getEditButtonProps().onClick?.(e);
      }}
      {...props}
    />
  );
};

RanaEditable.Preview = RanaEditablePreview;

/** Confirm / cancel buttons shown while editing. */
function EditableControls() {
  const { getSubmitButtonProps, getCancelButtonProps } = useEditableControls();

  return (
    <ButtonGroup size="sm" spacing={1} ml={1}>
      <IconButton
        aria-label="Confirm"
        icon={<CheckIcon color="brand.grey.600" />}
        {...getSubmitButtonProps()}
      />
      <IconButton
        aria-label="Cancel"
        icon={<CloseIcon boxSize={3} color="brand.grey.600" />}
        {...getCancelButtonProps()}
      />
    </ButtonGroup>
  );
}

const RanaEditableInput = (props: EditableInputProps) => {
  const { isEditing } = useEditableControls();
  if (!isEditing) return null;
  return (
    <Flex>
      {/* Prevent clicks inside the input from bubbling to parent row handlers */}
      <EditableInput
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        {...props}
      />
      <EditableControls />
    </Flex>
  );
};

RanaEditable.Input = RanaEditableInput;

const RanaEditableEditHandle = forwardRef<RanaEditableHandle>(function RanaEditableEditHandle(
  _,
  ref
) {
  // We don't actually need to provide a event here, but the typing does request it
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  type IgnoreReactMouseEvent = React.MouseEvent<any, MouseEvent>;
  const { getEditButtonProps } = useEditableControls();
  // onEdit is captured from the Chakra render prop and exposed via the handle.
  useImperativeHandle(ref, () => ({
    startEditing: () => getEditButtonProps().onClick?.({} as IgnoreReactMouseEvent),
  }));

  return null;
});

RanaEditable.EditHandle = RanaEditableEditHandle;
